import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { PublicProfileProvider, usePublicProfile } from "../../hooks/usePublicProfile";
import PageLayout from "./components/PageLayout";
import { Files, FileText, Mail } from "lucide-react";
import { AVAILABLE_DOCUMENTS } from "../../models/UserProfile";
import { Avatar } from "react-daisyui";
import { useState } from "react";

export const PublicProfileContent: React.FC = () => {
    const { publicProfile } = usePublicProfile();
    const [showAllAreas, setShowAllAreas] = useState(false);
    const [showContactModal, setShowContactModal] = useState(false);
    if (!publicProfile) return null;

    return (
        <div className="container mx-auto p-4 max-w-4xl">
            <div className="card bg-base-100 shadow-xl">
                <div className="card-body">
                    <div className="flex flex-col sm:flex-row gap-4 items-start">
                        <div className="flex-shrink-0 mr-4">
                            <Avatar size="lg" innerClassName="rounded" color="neutral" src={publicProfile.profile_picture_url || undefined} letters={publicProfile.name.split(' ').map(n => n[0]).join('')} />
                        </div>
                        <div>
                            <h2 className="card-title text-2xl">{publicProfile.name}</h2>
                            <div className="space-y-4">
                                <div className="badge badge-primary mt-2">{publicProfile.looking_for}</div>
                                <div>
                                    <h3 className="font-bold">Budget</h3>
                                    <p className="text-lg">up to {publicProfile.budget_max}€ per month</p>
                                </div>
                                
                                <div>
                                    <h3 className="font-bold">Preferred Areas</h3>
                                    <div className="flex flex-wrap gap-2 mt-1">
                                        {publicProfile.preferred_areas.slice(0, 5).map((area, index) => (
                                            <div key={index} className="badge badge-outline">{area}</div>
                                        ))}
                                        {publicProfile.preferred_areas.length > 5 && !showAllAreas && (
                                            <button 
                                                onClick={() => setShowAllAreas(true)}
                                                className="badge badge-outline cursor-pointer hover:bg-base-200"
                                            >
                                                +{publicProfile.preferred_areas.length - 3} more
                                            </button>
                                        )}
                                        {showAllAreas && (
                                            <>
                                                {publicProfile.preferred_areas.slice(3).map((area, index) => (
                                                    <div key={index} className="badge badge-outline">{area}</div>
                                                ))}
                                                <button 
                                                    onClick={() => setShowAllAreas(false)}
                                                    className="badge badge-outline cursor-pointer hover:bg-base-200"
                                                >
                                                    Show less
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="divider"></div>
                    
                    <div className="space-y-4">
                        <div>
                            <h3 className="font-bold">Apartment Requirements</h3>
                            <ul className="list-disc list-inside">
                                {publicProfile.apartment_size_min && <li>Size: {publicProfile.apartment_size_min }+ m²</li>}
                                <li>Rooms: {publicProfile.rooms_min ?? 1}+</li>
                                <li>Move-in date: {publicProfile.move_in_date ?? 'Flexible'}</li>
                                {publicProfile.min_contract_months && <li>Minimum contract: {publicProfile.min_contract_months} months</li>}
                            </ul>
                        </div>
                        
                        {publicProfile.soft_factors && (
                            <div>
                                <h3 className="font-bold">Additional Preferences</h3>
                                <p>{publicProfile.soft_factors}</p>
                            </div>
                        )}

                        {publicProfile.about_me && (
                            <div>
                                <h3 className="font-bold">About Me</h3>
                                <p>{publicProfile.about_me}</p>
                            </div>
                        )}
                    </div>

                    <div className="divider"></div>

                    <div className="space-y-4">
                        <h3 className="font-bold">Documents Available Upon Request</h3>
                        <div className="flex flex-wrap gap-4">
                            {publicProfile.documents_available.includes(AVAILABLE_DOCUMENTS.SCHUFA) && (
                                <div className="flex items-center p-4 border rounded-lg bg-base-200">
                                    <div className="mr-4">
                                        <FileText className="h-6 w-6" />
                                    </div>
                                    <div>
                                        <h4 className="font-semibold">SCHUFA Score</h4>
                                        <p className="text-sm text-gray-600">Credit rating document</p>
                                    </div>
                                </div>
                            )}

                            {publicProfile.documents_available.includes(AVAILABLE_DOCUMENTS.BEWERBERMAPPE) && (
                                <div className="flex items-center p-4 border rounded-lg bg-base-200">
                                    <div className="mr-4">
                                        <Files className="h-6 w-6" />
                                    </div>
                                    <div>
                                        <h4 className="font-semibold">Bewerbermappe</h4>
                                        <p className="text-sm text-gray-600">Complete application portfolio</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    
                    <div className="card-actions justify-end mt-4">
                        <button onClick={() => setShowContactModal(true)} className="btn btn-primary">
                            <Mail className="h-5 w-5 mr-2" />
                            Contact
                        </button>
                    </div>
                </div>
            </div>

            <dialog id="contact_modal" className={`modal ${showContactModal ? 'modal-open' : ''}`}>
                <div className="modal-box">
                    <h3 className="font-bold text-lg">Contact {publicProfile.name}</h3>
                    <p className="py-4">
                        Email:&nbsp;
                        <a href={`mailto:${publicProfile.email}`} className="link link-primary">
                            {publicProfile.email}
                        </a>
                    </p>
                    <div className="modal-action">
                        <button onClick={() => setShowContactModal(false)} className="btn">Close</button>
                    </div>
                </div>
                <form method="dialog" className="modal-backdrop">
                    <button onClick={() => setShowContactModal(false)}>close</button>
                </form>
            </dialog>
        </div>
    );
}

const PublicProfile: React.FC = () => {
    const { publicProfile, isLoading, error } = usePublicProfile();

    return (
        <div className="absolute min-h-screen w-full">
            <div className="absolute inset-0 bg-cover bg-center opacity-15" style={{backgroundImage: 'url("/app/bg-gradient.png")'}} />
            <PageLayout currentPage="publicProfile">
                {!error ? (
                    !isLoading ? (
                        <PublicProfileContent />
                    ) : (
                        <div className="flex justify-center items-center min-h-[50vh]">
                            <span className="loading loading-spinner loading-lg"></span>
                        </div>
                    )
                ) : (
                    <div className="flex flex-col items-center justify-center min-h-[50vh] text-center px-4">
                        <div className="text-red-500 mb-4">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                        </div>
                        <h2 className="text-xl font-semibold mb-2">Oops! Something went wrong</h2>
                        <p className="text-gray-600">We've been notified of this issue and are working to fix it. Please check back again soon.</p>
                    </div>
                )}
            </PageLayout>
        </div>
    );
}

const PublicProfilePage: React.FC = () => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const userKey = searchParams.get('user');

    return (
        <PublicProfileProvider userKey={userKey ?? undefined}>
            <PublicProfile />
        </PublicProfileProvider>
    );
};

export default PublicProfilePage;